import React, { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { solarAPI } from '../../services/api';
import { SolarPanel } from '../../types';

interface PanelSearchProps {
  selectedPanel: SolarPanel | null;
  onSelect: (panel: SolarPanel | null) => void;
  placeholder?: string;
  className?: string;
}

export const PanelSearch: React.FC<PanelSearchProps> = ({
  selectedPanel,
  onSelect,
  placeholder = "Search by brand, name, or SKU...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [panels, setPanels] = useState<SolarPanel[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTerm.length > 0) {
      searchPanels();
    } else {
      setPanels([]);
    }
  }, [searchTerm]);

  const searchPanels = async () => {
    setLoading(true);
    try {
      const results = await solarAPI.search({ search: searchTerm });
      setPanels(results.panels.slice(0, 40)); // Limit to 10 results for performance
    } catch (error) {
      console.error('Error searching panels:', error);
      setPanels([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    if (searchTerm && panels.length > 0) {
      setIsOpen(true);
    }
  };

  const handlePanelSelect = (panel: SolarPanel) => {
    onSelect(panel);
    setSearchTerm(panel.SKU + ' - ' + panel.Description);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleClear = () => {
    onSelect(null);
    setSearchTerm('');
    setPanels([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || panels.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < panels.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : panels.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < panels.length) {
          handlePanelSelect(panels[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLLIElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
        });
      }
    }
  }, [highlightedIndex]);

  const displayValue = selectedPanel 
    ? `${selectedPanel.SKU} - ${selectedPanel.Description}`
    : searchTerm;

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#63B56F] focus:border-transparent"
        />
        {(selectedPanel || searchTerm) && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {isOpen && (searchTerm.length > 0 || panels.length > 0) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#63B56F] mr-2"></div>
              Searching...
            </div>
          ) : panels.length > 0 ? (
            <ul ref={listRef} className="py-1">
              {panels.map((panel, index) => (
                <li
                  key={panel.id}
                  onClick={() => handlePanelSelect(panel)}
                  className={`px-4 py-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                    index === highlightedIndex
                      ? 'bg-[#63B56F] text-white'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex flex-col">
                    <div className="font-medium text-sm">
                      {panel.SKU}
                    </div>
                    <div className={`text-xs ${
                      index === highlightedIndex ? 'text-green-100' : 'text-gray-500'
                    }`}>
                      {panel.Description}
                    </div>
                    <div className={`text-xs mt-1 ${
                      index === highlightedIndex ? 'text-green-100' : 'text-gray-400'
                    }`}>
                      {panel.Watts_W}W • {panel.CellType} • {panel.ModuleEfficiency_percent}%
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : searchTerm.length > 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500">
              No panels found matching "{searchTerm}"
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
